/*
 * File: UpdateStudentPermitSchema.ts
 * Author: Austin Robinson
 * Date Created: 12/06/2025
 * Copyright (c) DrivingDoc 2025
 */

import { z } from 'zod';

export const UpdateStudentPermitSchema = z
  .object({
    permitNum: z.string().optional(),
    permitIssued: z.string().optional(),
    permitExpiration: z.string().optional(),
    permitRestrictions: z.string().optional(),
    permitEndorsements: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Cross-field validation: if any core field is provided, all must be provided
    const hasPermitNum = !!data.permitNum;
    const hasPermitIssued = !!data.permitIssued;
    const hasPermitExpiration = !!data.permitExpiration;

    if (hasPermitNum || hasPermitIssued || hasPermitExpiration) {
      if (!hasPermitNum) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['permitNum'],
          message: 'Required',
        });
      }
      if (!hasPermitIssued) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['permitIssued'],
          message: 'Required',
        });
      }
      if (!hasPermitExpiration) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['permitExpiration'],
          message: 'Required',
        });
      }
    }

    // Date validation: permitIssued must be <= today
    if (data.permitIssued && new Date(data.permitIssued) > new Date()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['permitIssued'],
        message: 'Permit issue date cannot be in the future.',
      });
    }

    // Date validation: permitExpiration must be > today
    if (data.permitExpiration && new Date(data.permitExpiration) <= new Date()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['permitExpiration'],
        message: 'Permit expiration date must be in the future.',
      });
    }
  })
  // Transform empty strings to null for proper API submission
  .transform((data) => ({
    permitNum: data.permitNum || null,
    permitIssued: data.permitIssued || null,
    permitExpiration: data.permitExpiration || null,
    permitRestrictions: data.permitRestrictions || null,
    permitEndorsements: data.permitEndorsements || null,
  }));

export type UpdateStudentPermitFormValues = z.infer<typeof UpdateStudentPermitSchema>;
